import { Lock } from '@mui/icons-material';
import Send from '@mui/icons-material/Send';
import { Stack, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useApi } from '../../../api/Api';
import { useRequest } from '../../../api/Request';
import { useAuth } from '../../../auth/Auth';
import { CommunityPost } from '../../../database/community';
import MentionInput, { MentionInputHandle } from '../../../components/MentionInput';

interface CommentEditorProps {
    post: CommunityPost;
    onSave: (post: CommunityPost) => void;
}

const CommentEditor: React.FC<CommentEditorProps> = ({ post, onSave }) => {
    const [comment, setComment] = useState('');
    const mentionInputRef = useRef<MentionInputHandle>(null);
    const commentRequest = useRequest();
    const api = useApi();
    const auth = useAuth();
    const user = auth.user;

    const onCreateComment = () => {
        const content = (mentionInputRef.current?.getSerializedValue() ?? comment).trim();
        if (content.length === 0) {
            return;
        }

        commentRequest.onStart();
        api.createComment(post.owner, post.createdAt, content)
            .then((response) => {
                onSave(response.data);
                setComment('');
                commentRequest.onSuccess('Successfully saved comment');
            })
            .catch((err) => {
                console.error('Create comment: ', err);
                commentRequest.onFailure(err);
            });
    };

    return (
        <Stack direction='row' spacing={1} alignItems='center' margin={2}>
            <MentionInput
                ref={mentionInputRef}
                size='small'
                inputProps={{ style: { fontSize: 14 } }}
                InputLabelProps={{ style: { fontSize: 14 } }}
                label={!user ? 'Sign in or up to comment' : 'Add your comment'}
                variant='outlined'
                multiline
                minRows={1}
                maxRows={7}
                fullWidth
                margin='normal'
                value={comment}
                onChange={setComment}
                disabled={commentRequest.isLoading()}
            />
            {commentRequest.isLoading() ? (
                <CircularProgress size={30} />
            ) : !user ? (
                <Tooltip title='Sign in or up to comment'>
                    <span>
                        <IconButton disabled>
                            <Lock />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : (
                <motion.div
                    whileTap={{ scale: 1.3, rotate: 15 }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                >
                    <IconButton type='submit' onClick={onCreateComment} color='primary'>
                        <Send />
                    </IconButton>
                </motion.div>
            )}
        </Stack>
    );
};
export default CommentEditor;
